import { SxProps } from '@mui/material'
import { scaledPx } from '@/styles'
import { iconSlot, panelWidth, railTransitionMs, railWidth } from './SidebarLayout.data'

interface SidebarLayoutStyles {
  root: SxProps
  asideBase: SxProps
  asideDesktop: SxProps
  asideDesktopExpanded: SxProps
  asideMobile: SxProps
  asideMobileClosed: SxProps
  backdrop: SxProps
  iconButton: SxProps
  iconButtonIcon: SxProps
  newSearchWrap: SxProps
  newSearch: SxProps
  newSearchRail: SxProps
  newSearchRound: SxProps
  itemIconSlot: SxProps
  itemIconSlotRail: SxProps
  newSearchIcon: SxProps
  newSearchLabel: SxProps
  railSpacer: SxProps
  footer: SxProps
  main: SxProps
  topBar: SxProps
  topBarBrand: SxProps
  content: SxProps
}

/**
 * Single horizontal gutter for every row in the panel. At 12px it also sets the
 * rail's width against the icon target (12 + 40 + 12 = 64), which is what keeps
 * every icon on the same vertical line in both states.
 */
export const sidebarGutter = 1.5

export const sidebarLayoutStyles: SidebarLayoutStyles = {
  root: {
    display: 'flex',
    height: '100dvh',
    width: '100%',
    position: 'relative',
    backgroundColor: 'background.secondary',
  },
  asideBase: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'background.default',
    overflow: 'hidden',
  },
  // Always mounted, and only ever changes width — nothing appears or disappears
  // around the content, so the page can never jump.
  asideDesktop: {
    width: scaledPx(railWidth),
    flexShrink: 0,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: 'border.secondary',
    transition: `width ${railTransitionMs}ms ease`,
  },
  asideDesktopExpanded: {
    width: scaledPx(panelWidth),
  },
  asideMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: scaledPx(panelWidth),
    maxWidth: '88%',
    // Below the MUI drawer (1200) / modal (1300) layer so dialogs open on top.
    zIndex: 1100,
    boxShadow: '0 8px 32px rgba(10, 13, 18, 0.18)',
    transition: `transform ${railTransitionMs}ms ease`,
  },
  asideMobileClosed: {
    transform: 'translateX(-100%)',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1099,
    backgroundColor: 'rgba(10, 13, 18, 0.4)',
  },
  iconButton: {
    width: scaledPx(36),
    height: scaledPx(36),
    flexShrink: 0,
    p: 0,
    borderRadius: 2,
    color: 'text.tertiary600',
    transition: 'background .2s, color .2s',
    '&:hover': {
      backgroundColor: 'utils.gray50',
      color: 'text.primary900',
    },
  },
  iconButtonIcon: {
    width: scaledPx(20),
    height: scaledPx(20),
  },
  newSearchWrap: {
    px: sidebarGutter,
    pb: 1,
  },
  // The mobile drawer's version, and the original: centred icon and label. It
  // slides in at full width and never changes size, so there is nothing for a
  // fixed icon column to protect against.
  newSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    width: '100%',
    minHeight: scaledPx(42),
    px: 1.5,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'border.secondary',
    backgroundColor: 'transparent',
    color: 'text.primary900',
    transition: 'background .2s, border-color .2s',
    '&:hover': {
      backgroundColor: 'utils.gray50',
      borderColor: 'border.primary',
    },
  },
  // The desktop rail's version: width follows the panel so the box grows on its
  // own, and the icon sits in a fixed slot at the left with padding that never
  // changes — which is what keeps it still while the box grows around it.
  newSearchRail: {
    justifyContent: 'flex-start',
    gap: 0,
    height: scaledPx(iconSlot),
    minHeight: 0,
    px: 0,
    transition: `border-radius ${railTransitionMs}ms ease, background .2s, border-color .2s`,
  },
  // Collapsed, only the corners change: a pill radius rather than 50% so the shape
  // stays sane at every width on the way — 50% would draw an ellipse.
  newSearchRound: {
    borderRadius: 999,
  },
  itemIconSlot: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Sized to the icon target less the button's border, so every glyph in the rail
  // centres on the same vertical line.
  itemIconSlotRail: {
    width: scaledPx(iconSlot - 2),
    height: '100%',
  },
  newSearchIcon: {
    width: scaledPx(16),
    height: scaledPx(16),
  },
  newSearchLabel: {
    typography: 'textSmSemibold',
    whiteSpace: 'nowrap',
  },
  // Holds the history's place in the rail, so the footer stays at the foot.
  railSpacer: {
    flex: 1,
    minHeight: 0,
  },
  footer: {
    mt: 'auto',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    px: sidebarGutter,
    py: 1,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: 'border.secondary',
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  // Mobile only: the rail can't afford 64px there, so the drawer overlays and
  // this bar carries the brand and the toggle instead.
  topBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 1,
    minHeight: scaledPx(56),
  },
  topBarBrand: {
    height: scaledPx(24),
    width: 'auto',
    display: 'block',
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}
